import ReactGA from 'react-ga4';
import { RoleDisplayMap } from 'constants/role';
import { BookFlipType } from 'constants/flipTypes';
import { groupPages, getBookSection } from 'util/book';
import path from 'path';
import { useCallback, useRef } from 'react';
import { useStore, StoreTypes } from 'context';
import { enqueue as enqueueOfflineGA, init as initOfflineGAQueue } from './offlineGAQueue';

// GA4 Measurement Protocol 設定（Electron 專用）
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA;
const GA_API_SECRET = process.env.REACT_APP_GA_API_SECRET;

// 判斷是否為 Electron 環境
const isElectronEnv = window.location.protocol === 'file:';

// 動態計算平台，區分 App 和 Web 來源
const getPlatform = () => {
  const { protocol } = window.location;
  const isWebProtocol = protocol === 'http:' || protocol === 'https:';

  if (window.ios) return 'iOS App';
  if (window.android) return 'Android App';
  if (isWebProtocol) return 'Web';
  return 'Electron';
};

// 產生或取得 Client ID（Electron Measurement Protocol 專用）
export const getClientId = () => {
  let clientId = localStorage.getItem('ga_client_id');
  if (!clientId) {
    clientId = 'electron_' + Date.now() + '_' + Math.random().toString(36).substring(2);
    localStorage.setItem('ga_client_id', clientId);
  }
  return clientId;
};

// Measurement Protocol 發送函數（Electron 專用）
const sendGA4Event = async (eventName, params) => {
  initOfflineGAQueue();
  if (!GA_MEASUREMENT_ID || !GA_API_SECRET) {
    console.warn('[GA4] Missing measurement_id or api_secret');
    return;
  }

  const finalParams = {
    ...params,
    ...(process.env.REACT_APP_LOG && { debug_mode: true }),
  };

  if (!navigator.onLine) {
    enqueueOfflineGA({ name: eventName, params: finalParams });
    return;
  }

  const payload = {
    client_id: getClientId(),
    events: [{
      name: eventName,
      params: finalParams,
    }],
  };

  try {
    const url = `https://www.google-analytics.com/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`;
    await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[GA4] Measurement Protocol error:', err);
  }
};

// 通用 GA 事件發送
// Electron: 使用英文事件名稱（Measurement Protocol 限制）
// 其他平台: 使用中文事件名稱
const sendGAEvent = (eventNameCN, eventNameEN, eventParams) => {
  if (isElectronEnv) {
    sendGA4Event(eventNameEN, eventParams);
  } else {
    const finalParams = process.env.REACT_APP_LOG
      ? { ...eventParams, debug_mode: true }
      : eventParams;
    ReactGA.event(eventNameCN, finalParams);
  }
};

// ===== 頁碼計算共用函數 =====

const getPageLabel = ({ book, catalog, pageIndex, isDoublePageMode, LRFlip }) => {
  const pageInfos = book && book.pageInfos;
  if (!pageInfos || !catalog || !catalog.getLabel) return pageIndex;

  const pages = pageInfos.reduce((acc, item) => {
    acc[item.pageIndex] = path.basename(item.html, path.extname(item.html));
    return acc;
  }, {});
  const grouped = groupPages(pages, isDoublePageMode);
  const pageArr = grouped[pageIndex];
  if (!pageArr) return pageIndex;

  const labels = pageArr.map((page) => {
    return catalog.getLabel(page - 1).toString();
  });
  if (labels.length === 2 && labels[0] !== labels[1]) {
    return LRFlip === BookFlipType.LEFT_TO_RIGHT
      ? labels[0] + '/' + labels[1]
      : labels[1] + '/' + labels[0];
  }
  return labels[0];
};

// ===== 新版 onebook_event 埋點 =====

const { userAgent } = navigator;

const getOsName = () => {
  // 明確的行動裝置 UA
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  // iPadOS 13+ 桌面模式會回報為 Mac，但有觸控
  if (/Mac/.test(userAgent) && navigator.maxTouchPoints > 1) return 'ios';
  // Android 平板桌面模式 UA 會變成 Linux，靠觸控判斷
  if (/Linux/.test(userAgent) && navigator.maxTouchPoints > 0) return 'android';
  // 桌面 OS
  if (/Windows/.test(userAgent)) return 'windows';
  if (/Mac/.test(userAgent)) return 'macos';
  return '';
};

const getBrowserName = () => {
  // 行動裝置/廠商瀏覽器（必須在 Chrome 之前檢查，因為它們的 UA 也包含 Chrome/）
  if (/SamsungBrowser\//.test(userAgent)) return 'samsung';
  if (/EdgA\/|EdgiOS\//.test(userAgent)) return 'edge';
  if (/CriOS\//.test(userAgent)) return 'chrome';
  if (/FxiOS\//.test(userAgent)) return 'firefox';
  // 桌面瀏覽器
  if (/Edg\//.test(userAgent)) return 'edge';
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/Chrome\//.test(userAgent)) return 'chrome';
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return 'safari';
  return '';
};

const gaOnebookEvent = ({ module, tapEvent, bookId, bookName, accountId, role, annotationId, pageIndex, interobjectType, interobjectId, src, bookSection, pageKey, pageLabel }) => {
  const platform = getPlatform();
  const eventParams = {
    platform,
    os_name: getOsName(),
    ...(platform === 'Web' && { browser_name: getBrowserName() }),
    ...(process.env.REACT_APP_VERSION && { version: process.env.REACT_APP_VERSION }),
    ...(bookId && {book_id: bookId}),
    ...(bookName && {book_name: bookName}),
    tap_event: tapEvent,
    ...(accountId && { account_id: accountId }),
    role: RoleDisplayMap[role] || '訪客',
    ...(annotationId && { annotation_id: annotationId }),
    ...(pageIndex && { page_index: String(pageIndex) }),
    ...(bookSection && { book_section: bookSection }),
    ...(pageKey && { page_key: pageKey }),
    ...(pageLabel && { page_label: pageLabel }),
    ...(interobjectType && { interobject_type: interobjectType }),
    ...(interobjectId && { interobject_id: interobjectId }),
    ...(src && { src }),
  };

  const eventName = module || 'onebook_event';
  sendGAEvent(eventName, eventName, eventParams);
};

// ===== useGAEvent hook（新版統一 GA 入口）=====

export const useGAEvent = () => {
  const [{ books, bookId, catalog }] = useStore(StoreTypes.books);
  const [{ pageIndex, isDoublePageMode }] = useStore(StoreTypes.reader);
  const [{ userId, isLogin, role }] = useStore(StoreTypes.user);
  const [{ annotationId }] = useStore(StoreTypes.annotation);

  const stateRef = useRef();
  stateRef.current = {
    books, bookId, catalog, isDoublePageMode,
    pageIndex, userId, isLogin, role, annotationId,
  };

  const gaEvent = useCallback((params) => {
    const callerParams = typeof params === 'string'
      ? { module: 'Reader', tapEvent: params }
      : params;

    const s = stateRef.current;
    const currentBook = s.books && s.bookId ? s.books.find((b) => b.bookId === s.bookId) : null;
    const LRFlip = currentBook && currentBook.LRFlip;
    const pageLabel = getPageLabel({
      book: currentBook,
      catalog: s.catalog,
      pageIndex: s.pageIndex,
      isDoublePageMode: s.isDoublePageMode,
      LRFlip,
    });

    const { section, bookName: sectionName } = getBookSection({
      catalog: s.catalog,
      pageIndex: s.pageIndex,
      isDoublePageMode: s.isDoublePageMode,
    });

    const defaults = {
      bookId: s.bookId || '',
      bookName: (currentBook && currentBook.displayName) || '',
      accountId: s.isLogin ? s.userId : '',
      role: s.role,
      annotationId: s.annotationId || '',
      pageIndex: pageLabel,
      bookSection: section,
      pageKey: `${s.bookId || ''}:${section}:${pageLabel}`,
      pageLabel: sectionName ? `${sectionName} p.${pageLabel}` : `p.${pageLabel}`,
    };

    gaOnebookEvent({ ...defaults, ...callerParams });
  }, []);

  return { gaEvent };
};
